import {Button, Divider, Input, Link} from "@heroui/react";
import {useMutation} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import {useForm} from '@mantine/form';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {GoogleLogin, useGoogleLogin} from '@react-oauth/google';
import TrustRow from './TrustRow.jsx';

export default function Login() {
	const navigate = useNavigate();
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isGoogleLoginLoading, setIsGoogleLoginLoading] = useState(false);
	
	useEffect(() => {
		if (localStorage.getItem('token')) {
			navigate('/app')
		}
	}, []);
	
	const onLoginSuccess = async (res) => {
		localStorage.setItem('token', res.token);
		navigate('/app');
	}
	
	const form = useForm({
		initialValues: {
			email: '',
			password: '',
		},
	});
	const onSubmit = useMutation({
		mutationFn: () => repositories.auth.login(form.values),
		onSuccess: (response) => {
			toast.success('Login successful')
			onLoginSuccess(response);
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
		}
	});
	
	const googleLoginAction = useMutation({
		mutationFn: repositories.auth.loginWithGoogle,
		onSuccess: onLoginSuccess,
		onError: (error) => toast.error(error?.response?.data?.detail || 'Something went wrong'),
		onSettled: () => {
			setIsGoogleLoginLoading(false);
		}
	})
	
	const googleLogin = useGoogleLogin({
		onSuccess: (tokenResponse) => {
			localStorage.removeItem('token');
			googleLoginAction.mutate(tokenResponse);
		},
		onError: (errorResponse) => {
			console.error("GOOGLE LOGIN ERROR");
			console.error(errorResponse);
			setIsGoogleLoginLoading(false);
		},
		onNonOAuthError: () => {
			toast.error('Google login cancelled');
			setIsGoogleLoginLoading(false);
		}
	})
	const onEnter = (e) => {
		if (e.key === 'Enter') onSubmit.mutate();
	};

	const inputClassNames = {
		base: 'group',
		label: 'text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 group-data-[focus=true]:text-primary group-data-[filled-within=true]:text-primary',
		inputWrapper: 'h-[58px] rounded-[14px] bg-[#f8f7ff] border-[1.5px] border-[#e0d9f7] shadow-none ' +
			'data-[hover=true]:bg-[#f8f7ff] ' +
			'group-data-[focus=true]:!bg-white group-data-[focus=true]:!border-primary ' +
			'group-data-[focus=true]:shadow-[0_0_0_3px_rgba(90,10,224,0.10)] transition-all',
		input: 'text-sm text-slate-900',
	};

	return (
		<>
			{/* heading */}
			<p className="auth-fade-1 text-[11px] font-bold text-primary uppercase tracking-[0.2em] mb-3">
				Welcome back
			</p>
			<h1 className="auth-fade-1 font-sans text-[32px] font-black text-slate-900 leading-tight tracking-tight mb-1">
				Sign in.
			</h1>
			<p className="auth-fade-1 text-sm text-slate-500 mb-8">
				Pick up where you left off — your studio is ready.
			</p>

			{/* Google */}
			<Button
				isLoading={isGoogleLoginLoading}
				disableRipple
				onPress={() => {
					setIsGoogleLoginLoading(true);
					googleLogin();
				}}
				className="auth-fade-2 w-full h-auto py-3.5 mb-5 rounded-2xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300">
				<img src="/images/google.svg" className="w-[18px]" alt=""/>
				Sign in with Google
			</Button>

			{/* divider */}
			<div className="auth-fade-2 flex items-center gap-3 mb-5">
				<div className="flex-1 h-px bg-slate-200"/>
				<span className="text-[11px] text-slate-400 font-bold tracking-widest">OR</span>
				<div className="flex-1 h-px bg-slate-200"/>
			</div>

			{/* email */}
			<div className="auth-fade-3 mb-4">
				<Input
					label="Email address"
					labelPlacement="inside"
					variant="flat"
					autoComplete="email"
					onKeyDown={onEnter}
					{...form.getInputProps('email')}
					classNames={inputClassNames}
				/>
			</div>

			{/* password */}
			<div className="auth-fade-3 mb-2">
				<Input
					label="Password"
					labelPlacement="inside"
					variant="flat"
					autoComplete="current-password"
					onKeyDown={onEnter}
					{...form.getInputProps('password')}
					type={isPasswordVisible ? 'text' : 'password'}
					endContent={
						<button
							type="button"
							aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
							onClick={() => setIsPasswordVisible(!isPasswordVisible)}
							className="text-slate-400 hover:text-slate-600 transition-colors">
							<i className={`eva ${isPasswordVisible ? 'eva-eye-outline' : 'eva-eye-off-2-outline'} text-xl`}/>
						</button>
					}
					classNames={inputClassNames}
				/>
			</div>

			{/* forgot */}
			<div className="auth-fade-3 flex justify-end mb-6">
				<button onClick={() => navigate('/forgot-password')}
				        className="text-[13px] font-semibold text-primary hover:underline">
					Forgot password?
				</button>
			</div>

			{/* submit */}
			<Button
				isLoading={onSubmit.isPending}
				disableRipple
				onPress={onSubmit.mutate}
				className="auth-fade-3 auth-btn-wrap w-full h-auto py-3.5 mb-5 rounded-2xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
				style={{background: 'linear-gradient(to right, #5A0AE0, #8C2CE2)', boxShadow: '0 6px 20px rgba(90,10,224,0.3)'}}
				endContent={!onSubmit.isPending && <i className="eva eva-arrow-forward-outline text-xl"/>}>
				<span className="auth-shimmer"/>
				Sign in
			</Button>

			<p className="auth-fade-4 text-center text-[13px] text-slate-500">
				Don't have an account?{' '}
				<Link href="/register" className="text-primary font-bold hover:underline cursor-pointer">
					Create one →
				</Link>
			</p>

			{/* trust row */}
			<TrustRow/>
		</>
	);
}
